import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, ShieldCheck, ShieldAlert, AlertCircle, CheckCircle2, Key, Download } from 'lucide-react';
import { usePostRequest, useGetRequest } from '@/shared/hooks/requests';
import { EIMZO_CREATE_CHALLENGE, EIMZO_VERIFY, EIMZO_CERTIFICATES } from '@/shared/utils/urls';
import { useEIMZO, eimzoHostHasApiKey } from '@/hooks/useEIMZO';
import { reportEImzoAvailability } from '@/hooks/eimzoAvailability';
import { maskNationalId } from '@/shared/utils/nationalId';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog';

/**
 * E-IMZO Key Setup Modal
 * For registering E-IMZO certificates for authenticated users
 */
export const EImzoSetupModal = ({ open, onOpenChange }) => {
    const { t, i18n } = useTranslation(['signing', 'common']);
    // init, loading, select, signing, success, unavailable (client not
    // installed/running), error (client reachable but something else failed)
    const [step, setStep] = useState('init');
    const [noCerts, setNoCerts] = useState(false);
    const [registeredCertificates, setRegisteredCertificates] = useState([]);

    const {
        connected,
        connecting,
        certificates,
        selectedCertificate,
        error: eimzoError,
        loadCertificates,
        loadKey,
        signData,
        setSelectedCertificate,
        connect,
    } = useEIMZO();

    const createChallenge = usePostRequest({ url: EIMZO_CREATE_CHALLENGE });
    const verifySignature = usePostRequest({ url: EIMZO_VERIFY });
    const getCertificates = useGetRequest({ url: EIMZO_CERTIFICATES });

    useEffect(() => {
        if (open) {
            setStep('init');
            initializeEImzo();
            loadRegisteredCertificates();
        }
    }, [open]);

    const loadRegisteredCertificates = async () => {
        const { success, response } = await getCertificates.request();
        if (success && response) {
            setRegisteredCertificates(response);
        }
    };

    const initializeEImzo = async () => {
        setStep('loading');
        setNoCerts(false);

        let clientReachable = false;
        try {
            // Connect to E-IMZO
            await connect();
            clientReachable = true;
            reportEImzoAvailability(true);
            // List available certificates
            const certList = await loadCertificates();

            if (certList.length === 0) {
                setNoCerts(true);
                setStep('error');
                return;
            }

            setStep('select');
        } catch (err) {
            console.error('E-IMZO initialization error:', err);
            if (!clientReachable) {
                // The desktop client never answered — a different problem
                // (and fix) than any post-connection failure.
                reportEImzoAvailability(false);
                setStep('unavailable');
            } else {
                setStep('error');
            }
        }
    };

    const handleRegisterCertificate = async () => {
        if (!selectedCertificate) {
            toast.error(t('signing:eimzo.selectCertificate'));
            return;
        }

        setStep('signing');

        try {
            // Step 1: Create challenge on backend
            const { success: challengeSuccess, response: challengeResponse } = await createChallenge.request({});

            if (!challengeSuccess || !challengeResponse?.challenge) {
                throw new Error(t('signing:interface.challengeFailed'));
            }

            // Step 2: Load key (will prompt user for password via E-IMZO UI)
            const keyId = await loadKey(selectedCertificate);

            // Step 3: Sign challenge with E-IMZO
            const pkcs7 = await signData(challengeResponse.challenge, keyId);

            // Step 4: Verify signature on backend and register certificate
            const { success: verifySuccess, response: verifyResponse } = await verifySignature.request({
                data: {
                    requestId: challengeResponse.requestId || challengeResponse.request_id || challengeResponse.id,
                    pkcs7: pkcs7
                }
            });

            if (!verifySuccess) {
                throw new Error(t('signing:eimzo.registerFailed'));
            }

            // Step 5: Success
            toast.success(t('signing:eimzo.registerSuccess'));
            setStep('success');

            // Reload registered certificates list
            await loadRegisteredCertificates();

            // Close modal after delay? Or just show success state.
            // Let's keep it open so user sees success message.

        } catch (err) {
            console.error('Registration error:', err);
            setStep('error');
            toast.error(err.message || t('signing:eimzo.registerFailed'));
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        try {
            const date = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;
            return date.toLocaleDateString({ ru: 'ru-RU', uz: 'uz-UZ', 'uz-Cyrl': 'uz-Cyrl-UZ' }[i18n.language] || 'en-US');
        } catch {
            return 'N/A';
        }
    };

    const isCertificateRegistered = (cert) => {
        if (!cert?.serialNumber) return false;
        return registeredCertificates.some(
            (registered) =>
                (registered.serialNumber || registered.serial_number) === cert.serialNumber
        );
    };

    // "No certificates" is what the module answers when this HOST has no
    // registered API key (-1022) — name that cause, it is a deployment
    // problem, not the user's.
    const displayError = noCerts
        ? (eimzoHostHasApiKey()
            ? t('signing:eimzo.noCertificates')
            : t('signing:eimzo.noApiKeyForHost', { host: window.location.hostname, interpolation: { escapeValue: false } }))
        : eimzoError || (step === 'error' ? t('signing:eimzo.connectionFailed') : null);

    // The header shield only turns green when the client actually answered.
    const headerIcon = step === 'unavailable'
        ? <ShieldAlert className="w-6 h-6 text-amber-500" />
        : ['select', 'signing', 'success'].includes(step)
            ? <ShieldCheck className="w-6 h-6 text-green-600" />
            : <ShieldCheck className="w-6 h-6 text-muted-foreground" />;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-x-hidden flex flex-col" zIndex={200}>
                <DialogHeader className="shrink-0">
                    <DialogTitle className="flex items-center gap-2 text-xl font-bold">
                        {headerIcon}
                        {t('signing:eimzo.setupTitle')}
                    </DialogTitle>
                    <DialogDescription>
                        {t('signing:eimzo.setupDescription')}
                    </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto px-1">
                    <div className="py-4 space-y-4">
                        {/* Already registered certificates info */}
                        {registeredCertificates.length > 0 && step === 'select' && (
                            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                                <p className="text-sm font-medium text-blue-900 dark:text-blue-200">
                                    {t('signing:eimzo.alreadyRegistered', { count: registeredCertificates.length })}
                                </p>
                            </div>
                        )}

                        {/* Loading State */}
                        {(step === 'loading' || connecting) && (
                            <div className="flex flex-col items-center justify-center py-8 text-center">
                                <Loader2 className="w-10 h-10 animate-spin text-primary mb-3" />
                                <p className="text-muted-foreground">{t('signing:eimzo.connect')}...</p>
                            </div>
                        )}

                        {/* Client not installed / not running */}
                        {step === 'unavailable' && (
                            <div className="space-y-4">
                                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md flex gap-3 text-amber-900 dark:text-amber-200">
                                    <ShieldAlert className="h-5 w-5 shrink-0" />
                                    <div className="text-sm">
                                        <p className="font-medium">{t('signing:eimzo.notInstalled')}</p>
                                        <p className="mt-0.5 opacity-80">{t('signing:eimzo.notInstalledHint')}</p>
                                    </div>
                                </div>
                                <div className="bg-muted p-3 rounded-md text-sm text-muted-foreground space-y-1">
                                    <p className="font-medium text-foreground">{t('signing:eimzo.setupInstructions')}</p>
                                    <ol className="list-decimal ml-4">
                                        <li>{t('signing:eimzo.setupStep1')}</li>
                                        <li>{t('signing:eimzo.setupStep2')}</li>
                                        <li>{t('signing:eimzo.setupStep3')}</li>
                                    </ol>
                                </div>
                                <div className="flex flex-col sm:flex-row gap-2">
                                    <Button asChild className="w-full sm:flex-1">
                                        <a href="https://e-imzo.uz/" target="_blank" rel="noreferrer">
                                            <Download className="w-4 h-4 mr-2" />
                                            {t('signing:eimzo.download')}
                                        </a>
                                    </Button>
                                    <Button onClick={initializeEImzo} variant="outline" className="w-full sm:flex-1">
                                        {t('signing:eimzo.tryAgain')}
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Error State */}
                        {step === 'error' && (
                            <div className="space-y-4">
                                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md flex gap-3 text-red-900 dark:text-red-200">
                                    <AlertCircle className="h-5 w-5 shrink-0" />
                                    <div className="text-sm break-words">
                                        {displayError ? displayError : t('signing:eimzo.connectionFailed')}
                                    </div>
                                </div>
                                <div className="bg-muted p-3 rounded-md text-sm text-muted-foreground space-y-1">
                                    <p className="font-medium text-foreground">{t('signing:eimzo.setupInstructions')}</p>
                                    <ol className="list-decimal ml-4">
                                        <li>{t('signing:eimzo.setupStep1')}</li>
                                        <li>{t('signing:eimzo.setupStep2')}</li>
                                        <li>{t('signing:eimzo.setupStep3')}</li>
                                    </ol>
                                </div>
                                <Button onClick={initializeEImzo} variant="outline" className="w-full">
                                    {t('signing:eimzo.tryAgain')}
                                </Button>
                            </div>
                        )}

                        {/* Certificate Selection */}
                        {step === 'select' && connected && (
                            <div className="space-y-4">
                                <div className="flex items-center gap-2 text-sm text-green-600 font-medium">
                                    <CheckCircle2 className="w-4 h-4" />
                                    {t('signing:eimzo.ready')}
                                </div>

                                <div className="space-y-2">
                                    {(() => {
                                        // Map local certificates for easier lookup
                                        const localSerials = new Set(certificates.map(c => c.serialNumber?.toLowerCase()));

                                        // Start with local certificates
                                        const displayList = [...certificates.map(c => ({ ...c, isOnDevice: true }))];

                                        // Add registered certificates that are NOT on device
                                        registeredCertificates.forEach(reg => {
                                            const serial = (reg.serialNumber || reg.serial_number || '').toLowerCase();
                                            if (serial && !localSerials.has(serial)) {
                                                displayList.push({
                                                    id: `api-${serial}`,
                                                    serialNumber: serial,
                                                    fullName: reg.subjectCommonName || reg.subject_common_name || reg.fullName || 'Unknown',
                                                    organization: reg.subjectRegion || reg.subject_region || reg.organization || '',
                                                    validTo: reg.validTo || reg.valid_to,
                                                    tin: reg.nationalIdValue || reg.national_id_value || reg.nationalIdMasked || reg.national_id_masked,
                                                    isOnDevice: false,
                                                    isRegistered: true
                                                });
                                            }
                                        });

                                        return displayList.map((cert) => {
                                            const isRegistered = cert.isRegistered || isCertificateRegistered(cert);
                                            const isSelected = selectedCertificate?.id === cert.id;
                                            const canSelect = cert.isOnDevice && !isRegistered && !cert.isExpired;

                                            return (
                                                <div
                                                    key={cert.id}
                                                    onClick={() => canSelect && setSelectedCertificate(cert)}
                                                    className={`p-3 border rounded-lg transition-all text-left group ${!cert.isOnDevice
                                                        ? 'bg-orange-50/20 opacity-90 border-orange-100'
                                                        : isRegistered
                                                            ? 'bg-gray-50 opacity-70 cursor-not-allowed border-gray-100'
                                                            : cert.isExpired
                                                                ? 'bg-red-50/50 opacity-60 cursor-not-allowed border-red-100'
                                                                : isSelected
                                                                    ? 'border-primary bg-primary/5 ring-1 ring-primary shadow-sm'
                                                                    : 'border-border hover:border-gray-300 cursor-pointer bg-white'
                                                        }`}
                                                >
                                                    <div className="flex justify-between items-start gap-3">
                                                        <div className="flex-1 min-w-0 space-y-1.5">
                                                            <div className="flex items-center gap-2">
                                                                <Key className={`w-3.5 h-3.5 shrink-0 ${cert.isOnDevice ? 'text-gray-400' : 'text-orange-400'}`} />
                                                                <p className="font-bold text-[13px] leading-tight text-gray-900 group-hover:text-primary transition-colors truncate">
                                                                    {cert.fullName}
                                                                </p>
                                                            </div>
                                                            <div className="text-[11px] text-gray-500 leading-relaxed font-medium">
                                                                {cert.organization && <p className="truncate opacity-80">{cert.organization}</p>}
                                                                <p className="flex flex-wrap gap-x-2">
                                                                    {(cert.tin || cert.TIN) && <span>{t('signing:candidates.pinfl')}: {maskNationalId(cert.tin || cert.TIN)}</span>}
                                                                    {(cert.pinfl || cert.PINFL) && <span>{t('signing:candidates.pinfl')}: {maskNationalId(cert.pinfl || cert.PINFL)}</span>}
                                                                </p>
                                                                <p className="text-gray-400">{t('signing:eimzo.validUntil')}: {formatDate(cert.validTo)}</p>
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-col gap-1.5 items-end shrink-0 pt-0.5">
                                                            {isSelected && <Badge className="bg-primary text-white text-[9px] px-1.5 h-4 border-none shadow-none">{t('signing:eimzo.selected')}</Badge>}
                                                            {isRegistered && <Badge variant="secondary" className="bg-gray-200 text-gray-600 text-[9px] px-1.5 h-4 border-none">{t('signing:eimzo.registered')}</Badge>}
                                                            {!cert.isOnDevice && (
                                                                <Badge variant="outline" className="text-[9px] px-1.5 h-4 border-orange-200 text-orange-600 bg-orange-50 whitespace-nowrap">
                                                                    {t('signing:eimzo.notOnDevice')}
                                                                </Badge>
                                                            )}
                                                            {cert.isExpired && <Badge variant="destructive" className="text-[9px] px-1.5 h-4 border-none">{t('signing:eimzo.expiredStatus')}</Badge>}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        });
                                    })()}
                                </div>

                                <Button
                                    onClick={handleRegisterCertificate}
                                    disabled={
                                        !selectedCertificate ||
                                        !selectedCertificate?.isOnDevice ||
                                        selectedCertificate?.isExpired ||
                                        isCertificateRegistered(selectedCertificate) ||
                                        createChallenge.loading ||
                                        verifySignature.loading
                                    }
                                    className="w-full mt-2"
                                >
                                    {createChallenge.loading || verifySignature.loading ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            {t('signing:eimzo.registering')}
                                        </>
                                    ) : (
                                        t('signing:eimzo.registerButton')
                                    )}
                                </Button>
                            </div>
                        )}

                        {/* Signing State */}
                        {step === 'signing' && (
                            <div className="flex flex-col items-center justify-center py-8 text-center space-y-3">
                                <div className="relative">
                                    <div className="w-12 h-12 rounded-full border-4 border-muted border-t-primary animate-spin"></div>
                                </div>
                                <div>
                                    <p className="font-medium">{t('signing:eimzo.signingAction')}</p>
                                    <p className="text-sm text-muted-foreground">{t('signing:eimzo.confirmInWindow')}</p>
                                </div>
                            </div>
                        )}

                        {/* Success State */}
                        {step === 'success' && (
                            <div className="flex flex-col items-center justify-center py-8 text-center space-y-4">
                                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold">{t('signing:eimzo.successTitle')}</h3>
                                    <p className="text-sm text-muted-foreground">{t('signing:eimzo.successDescription')}</p>
                                </div>
                                <Button onClick={() => onOpenChange(false)} variant="outline" className="min-w-[120px]">
                                    {t('common:close')}
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};
